/**
 * Utility to enhance location filters with remote options when filtering by Finnish locations
 * 
 * When a user filters by any location in Finland, we automatically include jobs that are:
 * - Remote - Europe
 * - Remote - Global
 * (or their language-specific equivalents)
 */

/**
 * Check if a location is in Finland
 */
function isFinlandLocation(location: string): boolean {
  if (!location) return false;
  const loc = location.toLowerCase().trim();
  
  // Check if it explicitly mentions Finland
  if (loc.includes('finland') || loc.includes('suomi')) {
    return true;
  }
  
  // List of major Finnish cities (without needing Finland suffix)
  const finnishCities = [
    'helsinki', 'espoo', 'tampere', 'vantaa', 'oulu', 'turku', 'jyväskylä',
    'lahti', 'kuopio', 'pori', 'joensuu', 'lappeenranta', 'hämeenlinna',
    'vaasa', 'seinäjoki', 'rovaniemi', 'mikkeli', 'kotka', 'salo', 'porvoo',
    'kouvola', 'hyvinkää', 'nurmijärvi', 'järvenpää', 'rauma', 'tuusula',
    'kirkkonummi', 'kajaani', 'kerava', 'naantali', 'savonlinna', 'ylivieska',
    'valkeakoski', 'hamina', 'nokia', 'riihimäki', 'imatra', 'raahe',
    'helsingfors', 'tammerfors', 'esbo', 'vanda', 'åbo' // Swedish names
  ];
  
  return finnishCities.some(city => loc.includes(city));
}

/**
 * Get remote location options for the given locale
 */
export function getRemoteLocationOptions(locale: string): string[] {
  const remoteOptions: Record<string, string[]> = {
    fi: [
      'Remote - Europe',
      'Remote - Global',
      'Etätyö - Eurooppa',
      'Etätyö - Maailmanlaajuinen'
    ],
    sv: [
      'Remote - Europe',
      'Remote - Global', 
      'Distans - Europa',
      'Distans - Global'
    ],
    en: [
      'Remote - Europe',
      'Remote - Global'
    ]
  };
  
  return remoteOptions[locale] || remoteOptions['en'];
}

/**
 * Enhance location filters by adding remote options when filtering by Finnish locations
 * 
 * @param locations - Array of location filters
 * @param locale - Current locale (en, fi, sv)
 * @returns Enhanced array of locations including remote options if applicable
 */
export function enhanceLocationFilters(locations: string[], locale: string = 'en'): string[] {
  if (!Array.isArray(locations) || locations.length === 0) {
    return locations;
  }
  
  // Check if any location is in Finland
  const hasFinlandLocation = locations.some(loc => isFinlandLocation(loc));
  
  if (!hasFinlandLocation) {
    return locations;
  }
  
  // Add remote options if not already present
  const remoteOptions = getRemoteLocationOptions(locale);
  const enhancedLocations = [...locations];
  
  remoteOptions.forEach(remoteOption => {
    // Only add if not already in the list
    if (!enhancedLocations.some(loc => 
      loc.toLowerCase().includes(remoteOption.toLowerCase())
    )) {
      enhancedLocations.push(remoteOption);
    }
  });
  
  return enhancedLocations;
}

/**
 * Build Algolia filter expression for enhanced location filters
 * Converts location array to OR filter string, automatically including remote options for Finnish locations
 * 
 * @param locations - Array of location filters
 * @param locale - Current locale
 * @returns Algolia filter string or empty string if no locations
 */
export function buildLocationFilterExpression(locations: string[], locale: string = 'en'): string {
  if (!Array.isArray(locations) || locations.length === 0) {
    return '';
  }
  
  const enhancedLocations = enhanceLocationFilters(locations, locale);
  
  if (enhancedLocations.length === 0) {
    return '';
  }
  
  if (enhancedLocations.length === 1) {
    return `location:"${enhancedLocations[0]}"`;
  }
  
  return `(${enhancedLocations.map(loc => `location:"${loc}"`).join(' OR ')})`;
}

